import type { Project } from './projects';

/**
 * Works carousel.
 *
 * Two modes, chosen at init:
 *  - "webgl": infinite draggable loop with inertia, modeled on
 *    thibautfoussard.com — the whole viewport bends as one sheet with drag
 *    velocity (screen-space parabola displacement), edges stretch vertically,
 *    chromatic aberration creeps in at the bottom, cards have rounded corners
 *    and a zoom-out + tilt on hover. Page scroll drifts the slider slightly.
 *  - "css":   native horizontal scroll with snap points. Used on touch
 *    devices, when prefers-reduced-motion is set, or when WebGL is missing.
 *
 * The DOM is identical in both modes, so keyboard access and semantics never
 * depend on the GPU.
 * ┌────────────────────────────────────────┬─────────────────────────────────────────────────┬──────────────────────────────────────────────────────────────────┐
 * │                  What                  │                      Where                      │                             Current                              │
 * ├────────────────────────────────────────┼─────────────────────────────────────────────────┼──────────────────────────────────────────────────────────────────┤
 * │ Total drift while scrolling in         │ DRIFT_DISTANCE constant                         │ 0.5 viewport widths — lower it if entry still feels fast         │
 * ├────────────────────────────────────────┼─────────────────────────────────────────────────┼──────────────────────────────────────────────────────────────────┤
 * │ Overall bend strength                  │ VERT: clamp(uVelocity * 0.2, -0.2, 0.2)         │ the 0.2s — lower both to soften every distortion at once         │
 * ├────────────────────────────────────────┼─────────────────────────────────────────────────┼──────────────────────────────────────────────────────────────────┤
 * │ Center bow depth                       │ VERT: w.x += strength * ... * uScreen.x * 0.5   │ the 0.5                                                          │
 * ├────────────────────────────────────────┼─────────────────────────────────────────────────┼──────────────────────────────────────────────────────────────────┤
 * │ Side lens                              │ VERT: float lens = 0.025 + abs(strength) * 0.5  │ 0.025 is the at-rest curve, 0.5 the drag response                │
 * ├────────────────────────────────────────┼─────────────────────────────────────────────────┼──────────────────────────────────────────────────────────────────┤
 * │ Bottom bow                             │ VERT: * 0.04                                    │ static, unrelated to velocity                                    │
 * ├────────────────────────────────────────┼─────────────────────────────────────────────────┼──────────────────────────────────────────────────────────────────┤
 * │ How fast the bend ramps up/down        │ loop: velocity += (... - velocity) * 0.12       │ lower to 0.06–0.08 for a lazier, smoother bend instead of a snap │
 * ├────────────────────────────────────────┼─────────────────────────────────────────────────┼──────────────────────────────────────────────────────────────────┤
 * │ How hard velocity spikes from movement │ loop: ((target - current) / viewportWidth) * 50 │ the 50 — lower means the same drag bends less                    │
 * └────────────────────────────────────────┴─────────────────────────────────────────────────┴──────────────────────────────────────────────────────────────────┘
 */

const VERT = /* glsl */`
#define PI 3.141592653589793
uniform float uVelocity;
uniform vec2 uScreen;
varying vec2 vUv;
varying vec2 vScreen;

// parabola(x, 1.) from the reference: 4x(1-x), peaks at the screen center
float parabola(float x) {
  x = clamp(x, 0.0, 1.0);
  return 4.0 * x * (1.0 - x);
}

void main() {
  vUv = uv;
  vec4 w = modelMatrix * vec4(position, 1.0);
  // Ortho camera maps world x:[0..W], y:[0..-H] to the screen, so
  // screen-space uv (0..1, y down) is just a division.
  vec2 s = vec2(w.x / uScreen.x, -w.y / uScreen.y);

  float strength = clamp(uVelocity * 0.2, -0.2, 0.2);

  // The whole sheet bows against the drag: strongest at screen center,
  // pinned at the edges — every card bends together, not individually.
  w.x += strength * parabola(s.y) * parabola(s.x) * uScreen.x * 0.2;

  // Lens: vertical stretch at the left/right stage edges. A small constant
  // base keeps the sides curved at rest; velocity deepens it while dragging.
  float rightAmp = smoothstep(0.5, 1.0, s.x);
  float leftAmp  = smoothstep(0.5, 1.0, 1.0 - s.x);
  float cy = (s.y - 0.5) * 2.0;
  float lens = 0.025 + abs(strength) * 0.9;
  w.y -= lens * (rightAmp + leftAmp) * cy * uScreen.y * 0.1;

  vScreen = s;
  gl_Position = projectionMatrix * viewMatrix * w;
}
`;

const FRAG = /* glsl */`
uniform sampler2D uMap;
uniform float uVelocity;
uniform float uZoom;
uniform float uPlaneAspect;
uniform float uImageAspect;
varying vec2 vUv;
varying vec2 vScreen;

void main() {
  // Rounded corners (SDF in card space, radius as a fraction of card height)
  float radius = 0.035;
  vec2 half_ = vec2(0.5 * uPlaneAspect, 0.5);
  vec2 p = (vUv - 0.5) * vec2(uPlaneAspect, 1.0);
  vec2 q = abs(p) - (half_ - radius);
  float d = length(max(q, 0.0)) - radius;
  float alpha = 1.0 - smoothstep(-0.002, 0.002, d);
  if (alpha <= 0.0) discard;

  // Cover fit, then hover zoom around the center
  vec2 uv = vUv;
  vec2 scale = uImageAspect > uPlaneAspect
    ? vec2(uPlaneAspect / uImageAspect, 1.0)
    : vec2(1.0, uImageAspect / uPlaneAspect);
  uv = (uv - 0.5) * scale * uZoom + 0.5;
  uv = clamp(uv, 0.001, 0.999);

  // Chromatic aberration along the drag, heavier toward the screen bottom
  float bottomAmp = 0.3 + smoothstep(0.6, 1.0, vScreen.y);
  float shift = uVelocity * 0.005 * bottomAmp;
  float r = texture2D(uMap, uv + vec2(shift, 0.0)).r;
  float g = texture2D(uMap, uv).g;
  float b = texture2D(uMap, uv - vec2(shift, 0.0)).b;
  gl_FragColor = vec4(r, g, b, alpha);
}
`;

function cardTemplate(p: Project, i: number): string {
  return `
  <li class="work-card">
    <button type="button" data-project="${p.slug}" aria-haspopup="dialog"
      aria-label="Open project: ${p.title}, ${p.category}">
      <figure>
        <div class="work-media">
          <img src="${p.image}" alt="" width="900" height="1200"
            ${i > 2 ? 'loading="lazy"' : 'fetchpriority="high"'} draggable="false">
        </div>
        <figcaption>
          <span class="work-name">${p.title}</span>
          <span class="work-meta">${p.date} - ${p.category}</span>
        </figcaption>
      </figure>
    </button>
  </li>`;
}

export function initCarousel(
  projects: Project[],
  onOpen: (slug: string, trigger: HTMLElement) => void,
): void {
  const section = document.getElementById('works');
  const viewport = document.getElementById('works-viewport');
  const track = document.getElementById('works-track');
  const canvas = document.getElementById('works-canvas') as HTMLCanvasElement | null;
  if (!section || !viewport || !track || !canvas) return;

  track.innerHTML = projects.map(cardTemplate).join('');
  track.querySelectorAll<HTMLButtonElement>('button[data-project]').forEach(btn => {
    btn.addEventListener('click', () => {
      // Suppress click that ends a drag
      if (section.dataset['dragged'] === 'true') return;
      onOpen(btn.dataset['project']!, btn);
    });
  });

  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  const hasWebGL = (() => {
    try {
      const c = document.createElement('canvas');
      return !!(c.getContext('webgl2') || c.getContext('webgl'));
    } catch { return false; }
  })();

  const hint = section.querySelector<HTMLElement>('.works-hint');
  if (!reducedMotion && finePointer && hasWebGL) {
    section.dataset['mode'] = 'webgl';
    initWebGLMode(section, viewport, track, canvas, projects);
  } else {
    section.dataset['mode'] = 'css';
    if (hint) hint.textContent = 'Swipe · Scroll';
  }
}

function initWebGLMode(
  section: HTMLElement,
  viewport: HTMLElement,
  track: HTMLElement,
  canvas: HTMLCanvasElement,
  projects: Project[],
): void {
  let current = 0;       // eased loop offset (px), unbounded — the loop wraps
  let target = 0;
  let velocity = 0;      // smoothed, for the shader

  const cards = Array.from(track.querySelectorAll<HTMLElement>('.work-card'));

  // Loop geometry, measured from the flex layout (card width + gap)
  let step = 1;
  let total = 1;
  const measure = () => {
    const first = cards[0]!;
    step = cards.length > 1 ? cards[1]!.offsetLeft - first.offsetLeft : first.offsetWidth;
    total = step * cards.length;
  };
  measure();

  // Wrapped x for card i at the current offset: cards cover [-step, total-step)
  const wrappedX = (i: number, offset: number) => {
    const base = i * step;
    return ((base - offset) % total + total + step) % total - step;
  };

  // ── Input: drag ───────────────────────────────────────────────────────────
  let dragging = false;
  let dragStartX = 0;
  let dragStartTarget = 0;
  let lastMoveX = 0;
  let lastMoveDx = 0;

  viewport.addEventListener('pointerdown', (e) => {
    dragging = true;
    dragStartX = e.clientX;
    lastMoveX = e.clientX;
    lastMoveDx = 0;
    dragStartTarget = target;
    section.dataset['dragged'] = 'false';
    viewport.classList.add('is-dragging');
  });
  viewport.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    const dx = e.clientX - dragStartX;
    if (Math.abs(dx) > 6 && section.dataset['dragged'] !== 'true') {
      section.dataset['dragged'] = 'true';
      // Capture only once an actual drag starts, so plain clicks still
      // reach the card buttons (capture retargets the click event).
      viewport.setPointerCapture(e.pointerId);
    }
    lastMoveDx = e.clientX - lastMoveX;
    lastMoveX = e.clientX;
    target = dragStartTarget - dx;
  });
  const endDrag = () => {
    if (!dragging) return;
    dragging = false;
    // Fling: carry the release velocity into extra travel
    target -= lastMoveDx * 14;
    viewport.classList.remove('is-dragging');
    // reset drag flag on next tick so the click handler sees it first
    setTimeout(() => { section.dataset['dragged'] = 'false'; }, 0);
  };
  viewport.addEventListener('pointerup', endDrag);
  viewport.addEventListener('pointercancel', endDrag);

  // ── Input: horizontal wheel/trackpad. Vertical wheel keeps scrolling the
  // page (the loop is infinite, hijacking it would trap the scroll). ────────
  viewport.addEventListener('wheel', (e) => {
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      e.preventDefault();
      target += e.deltaX;
    }
  }, { passive: false });

  // ── Scroll-in drift, like the reference site: while the section rises from
  // the bottom of the viewport to ~40% up, the loop drifts left-to-right by
  // scroll progress. Once fully in view (progress = 1) scroll stops driving
  // it and the slider is manual only (drag / trackpad). Scrolling back up
  // does nothing — the drift is one-directional. ────────────────────────────
  const DRIFT_DISTANCE = 0.2; // total drift while entering, in viewport widths
  let lastProgress = 0;
  const scrollProgress = () => {
    const r = section.getBoundingClientRect();
    return Math.min(Math.max((innerHeight - r.top) / (innerHeight * 0.6), 0), 1);
  };
  lastProgress = scrollProgress();
  window.addEventListener('scroll', () => {
    const p = scrollProgress();
    const dp = p - lastProgress;
    lastProgress = p;
    if (dp > 0 && !dragging) target -= dp * viewport.clientWidth * DRIFT_DISTANCE;
  }, { passive: true });

  // ── Input: keyboard - keep the focused card in view ──────────────────────
  const buttons = Array.from(track.querySelectorAll<HTMLButtonElement>('button[data-project]'));
  buttons.forEach((btn, i) => {
    btn.addEventListener('keydown', (e: KeyboardEvent) => {
      const next = e.key === 'ArrowRight' ? i + 1 : e.key === 'ArrowLeft' ? i - 1 : -1;
      if (next >= 0 && next < buttons.length) {
        e.preventDefault();
        buttons[next]!.focus();
      }
    });
    btn.addEventListener('focus', () => {
      // Only follow keyboard focus - pointer clicks also focus the button,
      // and snapping the track on click reads as a jump.
      if (!btn.matches(':focus-visible')) return;
      const x = wrappedX(i, target);
      const cardW = cards[i]!.offsetWidth;
      const vw = viewport.clientWidth;
      if (x < 40) target += x - 40;
      else if (x + cardW > vw - 40) target += x + cardW - (vw - 40);
    });
  });

  // ── Hover: zoom the image and tilt the plane, like the reference. ────────
  // Hit-tested per frame in the render loop (like his raycaster) instead of
  // pointerenter/leave, which get retargeted by drag pointer capture and can
  // miss or stick.
  const hover = cards.map(() => ({ zoom: 1, rot: 0, targetZoom: 1, targetRot: 0 }));
  const pointer = { x: -1, y: -1, inside: false };
  viewport.addEventListener('pointermove', (e) => {
    pointer.x = e.clientX;
    pointer.y = e.clientY;
    pointer.inside = true;
  });
  viewport.addEventListener('pointerleave', () => { pointer.inside = false; });

  // ── WebGL scene (Three.js is lazy-loaded when the section approaches) ────
  let started = false;
  const io = new IntersectionObserver((entries) => {
    if (entries.some(en => en.isIntersecting) && !started) {
      started = true;
      io.disconnect();
      void startScene();
    }
  }, { rootMargin: '400px' });
  io.observe(section);

  async function startScene(): Promise<void> {
    const THREE = await import('three');

    const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(devicePixelRatio, 2));

    const scene = new THREE.Scene();
    let W = 1, H = 1;
    const camera = new THREE.OrthographicCamera(0, 1, 0, -1, -100, 100);
    const screenSize = new THREE.Vector2(1, 1);

    // The canvas bleeds past the stage (CSS inset) so tilted/zoomed planes
    // aren't clipped at the stage edges; size the camera to the canvas, and
    // let CSS keep controlling its display size (updateStyle: false).
    const resize = () => {
      const c = canvas.getBoundingClientRect();
      W = c.width; H = c.height;
      renderer.setSize(W, H, false);
      camera.left = 0; camera.right = W; camera.top = 0; camera.bottom = -H;
      camera.updateProjectionMatrix();
      screenSize.set(W, H);
      measure();
    };
    window.addEventListener('resize', resize);
    resize();

    const loader = new THREE.TextureLoader();
    const medias = Array.from(track.querySelectorAll<HTMLElement>('.work-media'));
    const meshes = medias.map((el, i) => {
      const mat = new THREE.ShaderMaterial({
        vertexShader: VERT,
        fragmentShader: FRAG,
        transparent: true,
        uniforms: {
          uMap: { value: null as unknown },
          uVelocity: { value: 0 },
          uZoom: { value: 1 },
          uScreen: { value: screenSize },
          uPlaneAspect: { value: 1 },
          uImageAspect: { value: 1 },
        },
      });
      const tex = loader.load(projects[i]!.image, (t) => {
        const img = t.image as { width: number; height: number };
        mat.uniforms['uImageAspect']!.value = img.width / img.height;
      });
      tex.colorSpace = THREE.SRGBColorSpace;
      mat.uniforms['uMap']!.value = tex;
      const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1, 32, 32), mat);
      scene.add(mesh);
      return { el, mesh, mat };
    });

    canvas.classList.add('is-active');

    const canvasRect = () => canvas.getBoundingClientRect();

    const loop = () => {
      requestAnimationFrame(loop);

      // Inertia (same 0.075 lerp as the reference)
      current += (target - current) * 0.075;
      // Velocity signal normalized to viewport width so the distortion
      // strength is consistent across screen sizes
      const v = ((target - current) / Math.max(1, viewport.clientWidth)) * 50;
      velocity += (Math.max(-1, Math.min(1, v)) - velocity) * 0.06;

      // Position each card on the infinite loop (DOM captions follow along)
      cards.forEach((card, i) => {
        const x = wrappedX(i, current);
        card.style.transform = `translate3d(${x - i * step}px, 0, 0)`;
      });

      const s = canvasRect();
      meshes.forEach(({ el, mesh, mat }, i) => {
        const r = el.getBoundingClientRect();
        const x = r.left - s.left, y = r.top - s.top;
        const h = hover[i]!;
        const hovered = pointer.inside && !dragging
          && pointer.x >= r.left && pointer.x <= r.right
          && pointer.y >= r.top && pointer.y <= r.bottom;
        h.targetZoom = hovered ? 0.9 : 1;
        h.targetRot = hovered ? -0.075 : 0;
        h.zoom += (h.targetZoom - h.zoom) * 0.1;
        h.rot += (h.targetRot - h.rot) * 0.1;
        mesh.scale.set(r.width, r.height, 1);
        mesh.position.set(x + r.width / 2, -(y + r.height / 2), 0);
        mesh.rotation.z = h.rot;
        mat.uniforms['uPlaneAspect']!.value = r.width / Math.max(1, r.height);
        mat.uniforms['uVelocity']!.value = velocity;
        mat.uniforms['uZoom']!.value = h.zoom;
      });
      renderer.render(scene, camera);
    };
    loop();
  }
}
